import React, { useState, useMemo } from "react";

const legislacoes = [
  {
    legislacao: "Lei nº 8.160",
    ano: "1991",
    foco: "Institui o símbolo internacional da deficiência auditiva",
    categoria: "federal",
    tipo: "Lei"
  },
  {
    legislacao: "Lei nº 10.436",
    ano: "2002",
    foco: "Reconhece a Libras como meio legal de comunicação",
    categoria: "federal",
    tipo: "Lei"
  },
  {
    legislacao: "Decreto nº 5.626",
    ano: "2005",
    foco: "Regulamenta a Lei nº 10.436/2002. Define a educação bilíngue e a formação docente",
    categoria: "federal",
    tipo: "Decreto"
  },
  {
    legislacao: "Resolução CNE/CEB nº 4",
    ano: "2009",
    foco: "Diretrizes para o AEE e inclusão na escola regular",
    categoria: "federal",
    tipo: "Resolução"
  },
  {
    legislacao: "Lei nº 10.005 (PNE)",
    ano: "2014",
    foco: "Define metas para a educação até 2024",
    categoria: "federal",
    tipo: "Lei"
  },
  {
    legislacao: "Lei nº 13.146 (LBI)",
    ano: "2015",
    foco: "Estabelece direitos das pessoas com deficiência",
    categoria: "federal",
    tipo: "Lei"
  },
  {
    legislacao: "Lei nº 14.191",
    ano: "2021",
    foco: "Reconhece a educação bilíngue de surdos como modalidade autônoma",
    categoria: "federal",
    tipo: "Lei"
  }
];

export default function TabelaLegislacao() {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("");
  const [selectedLegislacao, setSelectedLegislacao] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);

  // Filtrar e ordenar legislações
  const filteredAndSortedLegislacoes = useMemo(() => {
    let filtered = legislacoes.filter(legislacao =>
      legislacao.legislacao.toLowerCase().includes(searchTerm.toLowerCase()) ||
      legislacao.foco.toLowerCase().includes(searchTerm.toLowerCase()) ||
      legislacao.tipo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      legislacao.ano.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortField === 'ano') {
      filtered.sort((a, b) => b.ano.localeCompare(a.ano));
    } else if (sortField === 'tipo') {
      filtered.sort((a, b) => a.tipo.localeCompare(b.tipo));
    }

    return filtered;
  }, [searchTerm, sortField]);

  const handleViewDetails = (legislacao) => {
    setSelectedLegislacao(legislacao);
    setIsModalOpen(true);
  };

  const handleExportLegislacao = (legislacao) => {
    const data = `Legislação: ${legislacao.legislacao}\nAno: ${legislacao.ano}\nFoco: ${legislacao.foco}\nTipo: ${legislacao.tipo}`;
    const blob = new Blob([data], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `legislacao_${legislacao.ano}_${legislacao.legislacao.replace(/[^a-zA-Z0-9]/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedLegislacao(null);
  };

  const getTipoColor = (tipo) => {
    switch(tipo) {
      case 'Lei': return { bg: '#dbeafe', color: '#1e40af' };
      case 'Decreto': return { bg: '#dcfce7', color: '#166534' };
      case 'Resolução': return { bg: '#fef3c7', color: '#92400e' };
      default: return { bg: '#f3f4f6', color: '#374151' };
    }
  };

  const getTipoIcon = (tipo) => {
    switch(tipo) {
      case 'Lei': return 'fas fa-gavel';
      case 'Decreto': return 'fas fa-file-signature';
      case 'Resolução': return 'fas fa-clipboard-list';
      default: return 'fas fa-file-alt';
    }
  };

  const styles = {
    container: {
      background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #e8eaf6 100%)',
      minHeight: '100vh',
      padding: '2rem 1rem',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
    },
    header: {
      textAlign: 'center',
      marginBottom: '2rem'
    },
    iconContainer: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '64px',
      height: '64px',
      background: 'linear-gradient(45deg, #dc2626, #ea580c)',
      borderRadius: '50%',
      marginBottom: '1rem'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 'bold',
      color: '#1f2937',
      marginBottom: '0.5rem',
      margin: '0 0 0.5rem 0'
    },
    subtitle: {
      fontSize: '1.125rem',
      color: '#6b7280',
      maxWidth: '42rem',
      margin: '0 auto',
      lineHeight: '1.6'
    },
    searchContainer: {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
      padding: '1.5rem',
      marginBottom: '2rem',
      maxWidth: '1200px',
      margin: '0 auto 2rem auto'
    },
    searchWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      alignItems: 'center'
    },
    inputContainer: {
      position: 'relative',
      flex: '1',
      width: '100%',
      maxWidth: '600px'
    },
    searchIcon: {
      position: 'absolute',
      left: '1rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af',
      zIndex: 1
    },
    input: {
      width: '100%',
      paddingLeft: '3rem',
      paddingRight: '1rem',
      paddingTop: '0.75rem',
      paddingBottom: '0.75rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.75rem',
      outline: 'none',
      transition: 'all 0.3s ease',
      fontSize: '1rem',
      boxSizing: 'border-box'
    },
    buttonContainer: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap'
    },
    button: (isActive) => ({
      padding: '0.75rem 1rem',
      borderRadius: '0.75rem',
      border: 'none',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s ease',
      background: isActive ? '#fecaca' : '#fee2e2',
      color: isActive ? '#991b1b' : '#dc2626',
      fontSize: '0.875rem',
      fontWeight: '500'
    }),
    tableContainer: {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      overflow: 'hidden',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    tableHeader: {
      background: 'linear-gradient(90deg, #dc2626 0%, #ef4444 50%, #ea580c 100%)',
      color: 'white',
      padding: '1.5rem'
    },
    headerContent: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    headerTitle: {
      fontSize: '1.5rem',
      fontWeight: 'bold',
      marginBottom: '0.25rem',
      margin: '0 0 0.25rem 0'
    },
    headerSubtitle: {
      color: '#fecaca',
      fontSize: '0.875rem',
      margin: 0
    },
    countContainer: {
      textAlign: 'right'
    },
    count: {
      fontSize: '1.875rem',
      fontWeight: 'bold',
      margin: 0
    },
    countLabel: {
      color: '#fecaca',
      fontSize: '0.875rem',
      margin: 0
    },
    tableWrapper: {
      overflowX: 'auto'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    thead: {
      background: 'linear-gradient(90deg, #f9fafb 0%, #fef2f2 100%)'
    },
    th: {
      padding: '1rem 1.5rem',
      textAlign: 'left',
      fontSize: '0.75rem',
      fontWeight: '600',
      color: '#374151',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    thContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    row: (isHovered) => ({
      borderBottom: '1px solid #f3f4f6',
      transition: 'all 0.3s ease',
      background: isHovered ? '#fef2f2' : 'white',
      transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
      boxShadow: isHovered ? '0 8px 25px rgba(220, 38, 38, 0.15)' : 'none'
    }),
    td: {
      padding: '1.25rem 1.5rem',
      verticalAlign: 'top'
    },
    legislacaoCell: {
      fontWeight: '600',
      color: '#dc2626',
      fontSize: '1rem'
    },
    anoCell: {
      fontWeight: '500',
      color: '#1f2937',
      fontSize: '1.125rem'
    },
    focoCell: {
      color: '#374151',
      lineHeight: '1.5',
      maxWidth: '300px'
    },
    tipoBadge: (tipo) => {
      const colors = getTipoColor(tipo);
      return {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.5rem 0.75rem',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: '500',
        background: colors.bg,
        color: colors.color,
        whiteSpace: 'nowrap'
      };
    },
    actionContainer: {
      display: 'flex',
      gap: '0.5rem'
    },
    actionButton: (color) => ({
      padding: '0.5rem',
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      background: 'transparent',
      color: color,
      fontSize: '1rem'
    }),
    footer: {
      background: 'linear-gradient(90deg, #f9fafb 0%, #fef2f2 100%)',
      padding: '1rem 1.5rem',
      borderTop: '1px solid #e5e7eb'
    },
    footerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '1rem',
      fontSize: '0.875rem'
    },
    footerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      color: '#4b5563',
      flexWrap: 'wrap'
    },
    footerItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    footerRight: {
      fontSize: '0.875rem',
      color: '#6b7280'
    },
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    },
    modal: {
      background: 'white',
      borderRadius: '1rem',
      maxWidth: '42rem',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      padding: '1.5rem'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '1rem'
    },
    modalTitle: {
      fontSize: '1.25rem',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    closeButton: {
      background: 'none',
      border: 'none',
      color: '#9ca3af',
      fontSize: '1.5rem',
      cursor: 'pointer',
      padding: '0.25rem'
    },
    modalContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem'
    },
    modalSection: {
      marginBottom: '1rem'
    },
    modalSectionTitle: {
      fontWeight: '600',
      color: '#374151',
      marginBottom: '0.5rem',
      margin: '0 0 0.5rem 0'
    },
    modalText: {
      color: '#1f2937',
      lineHeight: '1.6',
      margin: 0
    },
    modalGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.iconContainer}>
          <i className="fas fa-balance-scale" style={{color: 'white', fontSize: '1.5rem'}}></i>
        </div>
        <h1 style={styles.title}>Legislação Brasileira</h1>
        <p style={styles.subtitle}>
          Marco Legal da Educação Inclusiva - Leis e decretos sobre deficiência auditiva e educação de surdos
        </p>
      </div>

      <div style={styles.searchContainer}>
        <div style={styles.searchWrapper}>
          <div style={styles.inputContainer}>
            <i className="fas fa-search" style={styles.searchIcon}></i>
            <input 
              type="text" 
              placeholder="Buscar por legislação, foco ou tipo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.input}
            />
          </div>
          <div style={styles.buttonContainer}>
            <button 
              onClick={() => setSortField(sortField === 'ano' ? '' : 'ano')}
              style={styles.button(sortField === 'ano')}
            >
              <i className="fas fa-sort"></i>
              Ano
            </button>
            <button 
              onClick={() => setSortField(sortField === 'tipo' ? '' : 'tipo')}
              style={styles.button(sortField === 'tipo')}
            >
              <i className="fas fa-file-alt"></i>
              Tipo
            </button>
          </div>
        </div>
      </div>

      <div style={styles.tableContainer}>
        <div style={styles.tableHeader}>
          <div style={styles.headerContent}>
            <div>
              <h2 style={styles.headerTitle}>Marco Legal da Inclusão</h2>
              <p style={styles.headerSubtitle}>Legislação sobre educação de surdos no Brasil</p>
            </div>
            <div style={styles.countContainer}>
              <div style={styles.count}>{filteredAndSortedLegislacoes.length}</div>
              <div style={styles.countLabel}>Legislações</div>
            </div>
          </div>
        </div>

        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead style={styles.thead}>
              <tr>
                <th style={styles.th}>
                  <div style={styles.thContent}>
                    <i className="fas fa-gavel" style={{color: '#dc2626'}}></i>
                    Legislação
                  </div>
                </th>
                <th style={styles.th}>
                  <div style={styles.thContent}>
                    <i className="fas fa-calendar-alt" style={{color: '#ea580c'}}></i>
                    Ano
                  </div>
                </th>
                <th style={styles.th}>
                  <div style={styles.thContent}>
                    <i className="fas fa-bullseye" style={{color: '#059669'}}></i>
                    Foco para Inclusão
                  </div>
                </th>
                <th style={styles.th}>
                  <div style={styles.thContent}>
                    <i className="fas fa-tag" style={{color: '#7c3aed'}}></i>
                    Tipo
                  </div>
                </th>
                <th style={styles.th}>
                  <div style={styles.thContent}>
                    <i className="fas fa-cog" style={{color: '#6b7280'}}></i>
                    Ações
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedLegislacoes.map((legislacao, index) => (
                <tr 
                  key={index}
                  style={styles.row(hoveredRow === index)}
                  onMouseEnter={() => setHoveredRow(index)}
                  onMouseLeave={() => setHoveredRow(null)}
                >
                  <td style={styles.td}>
                    <div style={styles.legislacaoCell}>
                      {legislacao.legislacao}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.anoCell}>{legislacao.ano}</div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.focoCell}>
                      {legislacao.foco}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.tipoBadge(legislacao.tipo)}>
                      <i className={getTipoIcon(legislacao.tipo)}></i>
                      {legislacao.tipo}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actionContainer}>
                      <button 
                        onClick={() => handleViewDetails(legislacao)}
                        style={styles.actionButton('#dc2626')}
                        onMouseEnter={(e) => e.target.style.background = '#fecaca'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        title="Ver detalhes"
                      >
                        <i className="fas fa-eye"></i>
                      </button>
                      <button 
                        onClick={() => handleExportLegislacao(legislacao)}
                        style={styles.actionButton('#059669')}
                        onMouseEnter={(e) => e.target.style.background = '#d1fae5'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        title="Exportar"
                      >
                        <i className="fas fa-download"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={styles.footer}>
          <div style={styles.footerContent}>
            <div style={styles.footerLeft}>
              <span style={styles.footerItem}>
                <i className="fas fa-calendar" style={{color: '#dc2626'}}></i>
                Período: 1991 - 2021
              </span>
              <span style={styles.footerItem}>
                <i className="fas fa-flag-checkered" style={{color: '#059669'}}></i>
                Legislação Federal
              </span>
            </div>
            <div style={styles.footerRight}>
              Última atualização: <span style={{fontWeight: '500'}}>Dezembro 2024</span>
            </div>
          </div>
        </div>
      </div>

      {isModalOpen && selectedLegislacao && (
        <div style={styles.modalOverlay} onClick={closeModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Detalhes da Legislação</h3>
              <button onClick={closeModal} style={styles.closeButton}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div style={styles.modalContent}>
              <div style={styles.modalSection}>
                <h4 style={styles.modalSectionTitle}>Legislação</h4>
                <p style={styles.modalText}>{selectedLegislacao.legislacao}</p>
              </div>
              <div style={styles.modalGrid}>
                <div style={styles.modalSection}>
                  <h4 style={styles.modalSectionTitle}>Ano</h4>
                  <p style={styles.modalText}>{selectedLegislacao.ano}</p>
                </div>
                <div style={styles.modalSection}>
                  <h4 style={styles.modalSectionTitle}>Tipo</h4>
                  <span style={styles.tipoBadge(selectedLegislacao.tipo)}>
                    <i className={getTipoIcon(selectedLegislacao.tipo)}></i>
                    {selectedLegislacao.tipo}
                  </span>
                </div>
              </div>
              <div style={styles.modalSection}>
                <h4 style={styles.modalSectionTitle}>Foco para Inclusão</h4>
                <p style={styles.modalText}>{selectedLegislacao.foco}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}